import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Phone, Plus, Loader2, Check, Mail, Link2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';

export const SourcePhonesModal = ({ open, onOpenChange, onSaved, project }) => {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(false);
  const [edited, setEdited] = useState({});
  const [saving, setSaving] = useState({});
  const [newSource, setNewSource] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newSheet, setNewSheet] = useState('');

  const loadSources = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/source-phones', { params: { project } });
      setSources(res.data.phones || []);
      setEdited({});
    } catch (e) {
      toast.error('Erreur chargement');
    }
    setLoading(false);
  }, [project]);

  useEffect(() => {
    if (open) loadSources();
  }, [open, loadSources]);

  const getField = (source, field) => {
    if (edited[source]?.[field] !== undefined) return edited[source][field];
    const s = sources.find(x => x.source === source);
    return s?.[field] || '';
  };

  const setField = (source, field, value) => {
    setEdited(prev => ({
      ...prev,
      [source]: { ...(prev[source] || {}), [field]: value }
    }));
  };

  const hasChanges = (source) => {
    if (!edited[source]) return false;
    const s = sources.find(x => x.source === source);
    for (const [k, v] of Object.entries(edited[source])) {
      if (v !== (s?.[k] || '')) return true;
    }
    return false;
  };

  const handleSave = async (source) => {
    const payload = {
      source,
      project,
      phone: getField(source, 'phone'),
      email: getField(source, 'email'),
      sheet_url: getField(source, 'sheet_url')
    };
    setSaving(prev => ({ ...prev, [source]: true }));
    try {
      await api.post('/source-phones', payload);
      toast.success(`Sauvegarde OK pour ${source}`);
      setSources(prev => prev.map(s => s.source === source
        ? { ...s, phone: payload.phone, email: payload.email, sheet_url: payload.sheet_url }
        : s
      ));
      setEdited(prev => { const c = { ...prev }; delete c[source]; return c; });
      onSaved?.();
    } catch (e) {
      toast.error('Erreur sauvegarde');
    }
    setSaving(prev => ({ ...prev, [source]: false }));
  };

  const handleAdd = async () => {
    if (!newSource.trim()) return toast.error('Source requise');
    try {
      await api.post('/source-phones', { source: newSource.trim(), project, phone: newPhone.trim(), email: newEmail.trim(), sheet_url: newSheet.trim() });
      toast.success(`Source ajoutee: ${newSource}`);
      setNewSource('');
      setNewPhone('');
      setNewEmail('');
      setNewSheet('');
      loadSources();
      onSaved?.();
    } catch (e) {
      toast.error('Erreur ajout');
    }
  };

  const hasFilled = (s) => (s.phone || s.email || s.sheet_url);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-950 border-[#C4A052]/30 max-w-4xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-[#C4A052] text-lg flex items-center gap-2">
            <Phone className="h-5 w-5" />
            Sources — Infos Contact
          </DialogTitle>
        </DialogHeader>

        <p className="text-zinc-400 text-xs">Renseignez le telephone, email et lien Sheet pour chaque source.</p>

        {loading ? (
          <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-[#C4A052]" /></div>
        ) : (
          <>
            <div className="space-y-1.5 mt-2" data-testid="source-phones-list">
              {/* Header */}
              <div className="grid grid-cols-[160px_1fr_1fr_1fr_60px] gap-2 px-2 py-1 text-[10px] text-zinc-500 font-medium uppercase">
                <span>Source</span>
                <span className="flex items-center gap-1"><Phone className="h-3 w-3" /> Telephone</span>
                <span className="flex items-center gap-1"><Mail className="h-3 w-3" /> Email</span>
                <span className="flex items-center gap-1"><Link2 className="h-3 w-3" /> Lien Sheet</span>
                <span></span>
              </div>

              {sources.map((s) => {
                const changed = hasChanges(s.source);
                const filled = hasFilled(s);
                const isSaving = saving[s.source];

                return (
                  <div
                    key={s.source}
                    className={`grid grid-cols-[160px_1fr_1fr_1fr_60px] gap-2 items-center rounded-lg px-2 py-1.5 border ${filled ? 'bg-zinc-900/50 border-zinc-800' : 'bg-zinc-900/30 border-zinc-800/50'}`}
                    data-testid={`source-phone-row-${s.source}`}
                  >
                    <span className={`text-sm font-medium truncate ${filled ? 'text-white' : 'text-zinc-400'}`} title={s.source}>
                      {s.source}
                    </span>
                    <Input
                      value={getField(s.source, 'phone')}
                      onChange={(e) => setField(s.source, 'phone', e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter' && changed) handleSave(s.source); }}
                      placeholder="Numero..."
                      className="h-7 text-xs bg-zinc-800 border-zinc-700 text-emerald-400"
                    />
                    <Input
                      value={getField(s.source, 'email')}
                      onChange={(e) => setField(s.source, 'email', e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter' && changed) handleSave(s.source); }}
                      placeholder="Email..."
                      className="h-7 text-xs bg-zinc-800 border-zinc-700 text-sky-400"
                    />
                    <Input
                      value={getField(s.source, 'sheet_url')}
                      onChange={(e) => setField(s.source, 'sheet_url', e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter' && changed) handleSave(s.source); }}
                      placeholder="https://docs.google..."
                      className="h-7 text-xs bg-zinc-800 border-zinc-700 text-amber-400"
                    />
                    <Button
                      size="sm"
                      disabled={!changed || isSaving}
                      onClick={() => handleSave(s.source)}
                      className={`h-7 w-14 text-xs ${changed ? 'bg-emerald-600 hover:bg-emerald-500 text-white' : 'bg-zinc-800 text-zinc-500'}`}
                    >
                      {isSaving ? <Loader2 className="h-3 w-3 animate-spin" /> : <><Check className="h-3 w-3 mr-0.5" />OK</>}
                    </Button>
                  </div>
                );
              })}

              {sources.length === 0 && (
                <p className="text-zinc-500 text-sm text-center py-4">Aucune source trouvee</p>
              )}
            </div>

            {/* Add custom source */}
            <div className="mt-3 pt-3 border-t border-zinc-800">
              <p className="text-zinc-500 text-xs mb-2">Ajouter une source manuellement :</p>
              <div className="grid grid-cols-[160px_1fr_1fr_1fr_40px] gap-2">
                <Input
                  placeholder="Source"
                  value={newSource}
                  onChange={(e) => setNewSource(e.target.value)}
                  className="bg-zinc-900 border-zinc-700 text-white text-sm h-8"
                />
                <Input
                  placeholder="Telephone"
                  value={newPhone}
                  onChange={(e) => setNewPhone(e.target.value)}
                  className="bg-zinc-900 border-zinc-700 text-white text-sm h-8"
                />
                <Input
                  placeholder="Email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  className="bg-zinc-900 border-zinc-700 text-white text-sm h-8"
                />
                <Input
                  placeholder="Lien Sheet"
                  value={newSheet}
                  onChange={(e) => setNewSheet(e.target.value)}
                  className="bg-zinc-900 border-zinc-700 text-white text-sm h-8"
                />
                <Button onClick={handleAdd} size="sm" className="bg-emerald-600 hover:bg-emerald-500 shrink-0 h-8">
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SourcePhonesModal;
